import { type ReactElement, type ReactNode } from 'react';

import { cn } from '@apps/web/lib/utils';

type TimelineEventRowProps = {
    readonly icon: ReactNode;
    readonly children: ReactNode;
    readonly tone?: 'muted' | 'danger' | 'success';
    readonly ariaLabel?: string;
    readonly className?: string;
};

// Canonical event-line anatomy shared by conversations, calls, and agent chat.
// The fixed icon lane keeps sentence starts aligned when neighboring events use
// different glyphs, matching the v3 gallery's quiet one-line treatment.
export function TimelineEventRow({
    icon,
    children,
    tone = 'muted',
    ariaLabel,
    className,
}: TimelineEventRowProps): ReactElement {
    return (
        <div
            aria-label={ariaLabel}
            className={cn(
                'flex items-center gap-2 px-2 py-1 text-[12.5px] leading-[19px]',
                tone === 'muted' && 'text-muted-foreground',
                tone === 'danger' && 'text-red-600 dark:text-red-400',
                tone === 'success' && 'text-muted-foreground',
                className,
            )}
            data-slot="timeline-event-row"
            data-testid="timeline-event-row"
        >
            <span
                className={cn(
                    'flex w-[15px] shrink-0 items-center justify-center [&_svg]:size-[13px] [&_svg]:shrink-0',
                    tone === 'success' && 'text-emerald-700',
                )}
            >
                {icon}
            </span>
            {children}
        </div>
    );
}
